import { AnswerState, Correctness, GradeResult, ScenarioQuestion } from '../models/scenario.model';

const CONFIDENCE_LEVELS = ['Guessing', 'Fairly sure', 'Certain'];

/** Is the student's input complete enough to submit? */
export function isReady(q: ScenarioQuestion, a: AnswerState): boolean {
  switch (q.kind) {
    case 'multiple-response':
    case 'hazard-audit': return (a.multi || []).length > 0;
    case 'true-false-round': return !!a.tf && a.tf.length === (q.statements || []).length && a.tf.every(v => v !== null && v !== undefined);
    case 'cloze': return !!a.blanks && a.blanks.every(v => v !== null && v !== undefined);
    case 'answer-rationale': return a.choice != null && a.why != null;
    case 'confidence': return a.choice != null && a.confidence != null;
    case 'sequencing':
    case 'ranked':
    case 'slider': return true;
    case 'matching': return Object.keys(a.match || {}).length === (q.pairs || []).length;
    case 'numeric':
    case 'chart-lookup': return !isNaN(parseNumber(a.text));
    case 'radio-phraseology': return (a.line || []).length > 0;
    case 'diversion-plot': return !!a.airport && !(q.airports || []).some(p => p.origin && p.id === a.airport);
    case 'timed': return a.choice != null || !!a.expired;
    default: return a.choice != null;
  }
}

export function grade(q: ScenarioQuestion, a: AnswerState): GradeResult {
  const pts = q.points || 0;
  const explanation = q.explanation || '';
  const byFraction = (hits: number, total: number, title: string): GradeResult => {
    const frac = total ? Math.max(0, hits) / total : 0;
    return { earned: round(pts * frac), max: pts, correctness: level(frac), title, text: explanation };
  };

  switch (q.kind) {
    case 'multiple-response': {
      const correct = indexesWhere(q.choices, c => c.correct);
      const sel = a.multi || [];
      const hits = sel.filter(i => correct.includes(i)).length;
      const net = hits - (sel.length - hits);
      return byFraction(net, correct.length, `${Math.max(0, net)} of ${correct.length} correct`);
    }
    case 'hazard-audit': {
      const hazards = indexesWhere(q.hotspots, h => h.correct);
      const sel = a.multi || [];
      const found = sel.filter(i => hazards.includes(i)).length;
      const fp = sel.length - found;
      const r = byFraction(found - fp, hazards.length, `${found} found · ${hazards.length - found} missed · ${fp} false positive${fp === 1 ? '' : 's'}`);
      if (found === hazards.length && fp === 0) { r.correctness = 'ok'; }
      return r;
    }
    case 'true-false-round': {
      const s = q.statements || [];
      const n = s.filter((st, i) => (a.tf || [])[i] === st.answer).length;
      return byFraction(n, s.length, `${n} of ${s.length} correct`);
    }
    case 'cloze': {
      const ans = q.clozeAnswers || [];
      const n = ans.filter((t, i) => (a.blanks || [])[i] === t).length;
      return byFraction(n, ans.length, `${n} of ${ans.length} blanks correct`);
    }
    case 'answer-rationale': {
      const aOk = isCorrectChoice(q.choices, a.choice), wOk = isCorrectChoice(q.whys, a.why);
      const earned = (aOk ? pts / 2 : 0) + (aOk && wOk ? pts / 2 : 0);
      const title = aOk && wOk ? `Both correct · ${pts} pts` : aOk ? `Right action, wrong reason · ${round(pts / 2)} pts` : 'Action incorrect';
      return { earned: round(earned), max: pts, correctness: aOk && wOk ? 'ok' : aOk ? 'part' : 'bad', title, text: explanation };
    }
    case 'confidence': {
      const ok = isCorrectChoice(q.choices, a.choice);
      const certain = a.confidence === 2;
      const label = CONFIDENCE_LEVELS[a.confidence || 0].toLowerCase();
      return {
        earned: ok ? pts : 0, max: pts, correctness: ok ? 'ok' : certain ? 'bad' : 'part', flagged: !ok && certain,
        title: ok ? `Correct · ${label}` : certain ? 'Incorrect and certain — flagged' : `Incorrect · ${label}`,
        text: explanation,
      };
    }
    case 'sequencing':
    case 'ranked': {
      const order = a.order || [];
      const n = order.filter((item, pos) => item === pos).length;
      return byFraction(n, (q.items || []).length, `${n} of ${(q.items || []).length} in position`);
    }
    case 'matching': {
      const pairs = q.pairs || [];
      const n = pairs.filter((p, i) => (a.match || {})[i] === i).length;
      return byFraction(n, pairs.length, `${n} of ${pairs.length} pairs correct`);
    }
    case 'numeric':
    case 'chart-lookup':
    case 'slider': {
      const val = q.kind === 'slider' ? a.value : parseNumber(a.text);
      const diff = Math.abs((val as number) - (q.answer || 0));
      const ok = !isNaN(diff) && diff <= (q.tolerance || 0);
      return { earned: ok ? pts : 0, max: pts, correctness: ok ? 'ok' : 'bad', text: explanation,
        title: ok ? `Within tolerance · ${pts} pts` : isNaN(diff) ? 'No answer' : `Off by ${round(diff)}${q.unit === '°' ? '°' : ' ' + (q.unit || '')}` };
    }
    case 'radio-phraseology': {
      const elements = q.elements || [];
      const used = (a.line || []).map(i => (q.phrases || [])[i]).filter(Boolean);
      const hits = elements.filter((_, ei) => used.some(p => p.element === ei)).length;
      const nonStd = used.filter(p => p.element < 0).length;
      const r = byFraction(hits - nonStd * 0.5, elements.length,
        `${hits} of ${elements.length} elements${nonStd ? ` · ${nonStd} non-standard phrase${nonStd > 1 ? 's' : ''}` : ''}`);
      if (hits === elements.length && !nonStd) { r.correctness = 'ok'; }
      return r;
    }
    case 'diversion-plot': {
      const ok = a.airport === q.correctAirport;
      return { earned: ok ? pts : 0, max: pts, correctness: ok ? 'ok' : 'bad', title: ok ? `Good diversion · ${pts} pts` : 'Not a workable alternate', text: explanation };
    }
    case 'cockpit-control': {
      const spot = (q.hotspots || [])[a.choice as number];
      const ok = !!spot && !!spot.correct;
      return { earned: ok ? pts : 0, max: pts, correctness: ok ? 'ok' : 'bad',
        title: ok ? `Correct · ${pts} pts` : 'Wrong control — consequence applied',
        text: ok ? explanation : `${spot && spot.why ? spot.why + ' ' : ''}${explanation}` };
    }
    case 'timed':
      if (a.expired && a.choice == null) {
        return { earned: 0, max: pts, correctness: 'bad', title: 'No action · 0 pts', text: `Time ran out. Indecision is itself a decision here. ${explanation}` };
      }
      return choiceResult(q, a, pts, explanation);
    default:
      return choiceResult(q, a, pts, explanation);
  }
}

function choiceResult(q: ScenarioQuestion, a: AnswerState, pts: number, explanation: string): GradeResult {
  const ok = isCorrectChoice(q.choices, a.choice);
  const why = !ok && q.choices && a.choice != null ? q.choices[a.choice].why : '';
  return { earned: ok ? pts : 0, max: pts, correctness: ok ? 'ok' : 'bad', title: ok ? `Correct · ${pts} pts` : 'Incorrect', text: why || explanation };
}

function isCorrectChoice(list: { correct?: boolean }[] | undefined, i: number | undefined): boolean {
  return i != null && !!list && !!list[i] && !!list[i].correct;
}

function indexesWhere<T>(list: T[] | undefined, pred: (t: T) => boolean): number[] {
  return (list || []).map((t, i) => (pred(t) ? i : -1)).filter(i => i >= 0);
}

function level(frac: number): Correctness {
  return frac >= 1 ? 'ok' : frac > 0 ? 'part' : 'bad';
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

export function parseNumber(text: string | undefined): number {
  const cleaned = (text || '').replace(/[, ]/g, '').trim();
  return cleaned === '' ? NaN : Number(cleaned);
}
